/**
 * Gemini Live input-transcription settings a deployment can store.
 *
 * Mirrors `setup.inputAudioTranscription` (`AudioTranscriptionConfig`) on the
 * Live API. Whether a model takes it is its `customVocabulary` row in the
 * per-model tunables table (`GeminiLiveTunables.schema.ts`).
 *
 * @see https://ai.google.dev/api/live — `AudioTranscriptionConfig`
 */

import { z } from "zod";

/**
 * Most phrases one deployment may store. Google publishes no limit on
 * `customVocabulary[]`; this cap is ours, so a write cannot grow the setup
 * message without bound.
 */
export const GEMINI_CUSTOM_VOCABULARY_MAX_TERMS = 100;

/** Longest single phrase, in characters. Ours, for the same reason. */
export const GEMINI_CUSTOM_VOCABULARY_MAX_TERM_CHARS = 100;

/**
 * `inputAudioTranscription.customVocabulary[]` — "custom vocabulary phrases to
 * bias the speech recognition model toward recognizing specific terms (product
 * names, proper nouns, jargon)". One phrase per entry, as the API takes them.
 *
 * Absent means no bias. This one schema is both the read and the write shape,
 * and the voice editor renders nothing when its read fails — so a cap may be
 * RAISED once data exists, never lowered below a value already stored.
 */
export const GeminiCustomVocabularySchema = z
  .array(z.string().trim().min(1).max(GEMINI_CUSTOM_VOCABULARY_MAX_TERM_CHARS))
  .max(GEMINI_CUSTOM_VOCABULARY_MAX_TERMS)
  .optional();

export type GeminiCustomVocabulary = NonNullable<z.infer<typeof GeminiCustomVocabularySchema>>;
